import { useMemo, useContext } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { PermissionsContext } from "context/AppProvider/PermissionsContext";
import { PERMISSIONS } from "constants/portal";
import { allowAction, permissionChecker } from "helpers/common";
import { useDetermineShareFlowUserAccessType } from "hooks/useDetermineShareFlowUserAccessType";
import { useUserRole } from "./useUserRole";

export const useFlowPermissions = (item) => {
  const { user } = useAuth0();
  const { userPermissions } = useContext(PermissionsContext);
  const { isEditor } = useDetermineShareFlowUserAccessType(item?.id);
  const { isOwnerRole, isAdminRole } = useUserRole();

  const permissions = useMemo(() => {
    if (!user || !item) {
      return {
        canEdit: false,
        canShare: false,
        canRetrieve: false,
        canDelete: false,
        canActivate: false,
        canDeactivate: false,
        canCreate: false,
        canExport: false,
        isOwner: false,
        canClone: false
      };
    }

    const isOwner = allowAction(user?.cxfUserId, item?.created_by_id);
    const canEdit =
      permissionChecker(userPermissions, PERMISSIONS.EDIT_ANY_WORKFLOWS) ||
      ((isOwner || isEditor) && permissionChecker(userPermissions, PERMISSIONS.EDIT_WORKFLOWS));

    return {
      canEdit,

      canShare:
        permissionChecker(userPermissions, PERMISSIONS.SHARE_ANY_WORKFLOWS) ||
        (isOwner && permissionChecker(userPermissions, PERMISSIONS.SHARE_WORKFLOWS)),

      canRetrieve: permissionChecker(userPermissions, PERMISSIONS.RETRIEVE_WORKFLOWS),

      canDelete:
        permissionChecker(userPermissions, PERMISSIONS.DELETE_ANY_WORKFLOWS) ||
        (isOwner && permissionChecker(userPermissions, PERMISSIONS.DELETE_WORKFLOWS)),

      canActivate:
        isOwnerRole ||
        ((isOwner || isEditor) &&
          permissionChecker(userPermissions, PERMISSIONS.ACTIVATE_WORKFLOWS)),

      canDeactivate:
        isOwnerRole ||
        ((isOwner || isEditor) &&
          permissionChecker(userPermissions, PERMISSIONS.DEACTIVATE_WORKFLOWS)),

      canCreate:
        (isOwner || isEditor) && permissionChecker(userPermissions, PERMISSIONS.CREATE_WORKFLOWS),

      canExport: isOwner || isEditor || canEdit,

      isOwner,
      canClone: isOwner || isEditor || isOwnerRole || isAdminRole
    };
  }, [user, item, userPermissions, isEditor, isOwnerRole, isAdminRole]);

  return permissions;
};
